import {
  type UploadCsvResult,
  type UploadCsvVariables,
  type UseAnalyzeCsvStreamReturn,
  useAnalyzeCsvStream,
  useUploadCsv,
} from '@front/hook/Api/File';
import type { FileUploaderItem } from '@plaidev/sour-react';
import { useToast } from '@plaidev/sour-react';
import type { UseMutationResult } from '@tanstack/react-query';
import { AuthDomain } from 'auth-lib-front';
import { type Dispatch, type SetStateAction, useState } from 'react';
import type { UseFormGetValues, UseFormReset } from 'react-hook-form';
import { CSV_ANALYZE_STREAM_DEFAULT_MODEL_ID, type CsvAnalysis, type CsvAnalyzeStreamModelId } from 'troppus-common/shared';
import { buildConfigureFormPatchFromAnalysis } from '../applyAnalysis';
import { resetColumnRelatedFields } from '../formHelpers';
import type { FormValues } from '../types';

import Project = AuthDomain.Object.Project;

export type UseAnalyzeFlowReturn = {
  step: 'upload' | 'analyzing' | 'configure';
  thinking: string;
  analysis: CsvAnalysis | null;
  uploadMutation: UseMutationResult<UploadCsvResult, Error, UploadCsvVariables>;
  analyzeStream: UseAnalyzeCsvStreamReturn;
  analyzeModelId: CsvAnalyzeStreamModelId;
  setAnalyzeModelId: Dispatch<SetStateAction<CsvAnalyzeStreamModelId>>;
  uploadItems: FileUploaderItem[];
  selectedFile: File | null;
  uploadedFileId: string | null;
  uploadProgress: number | null;
  handleSelectFiles: (files: FileList) => void;
  handleDeleteUpload: (_fileId: string) => void;
  handleStartUpload: () => Promise<void>;
  handleHeaderNavigateAway: () => void;
};

type Params = {
  projectId: Project.Id;
  isSystemAdmin: boolean;
  getValues: UseFormGetValues<FormValues>;
  reset: UseFormReset<FormValues>;
  navigateToList: () => void;
};

export function useAnalyzeFlow({ projectId, isSystemAdmin, getValues, reset, navigateToList }: Params): UseAnalyzeFlowReturn {
  const toast = useToast();
  const uploadMutation = useUploadCsv();
  const analyzeStream = useAnalyzeCsvStream(projectId);

  const [analyzeModelId, setAnalyzeModelId] = useState<CsvAnalyzeStreamModelId>(() => CSV_ANALYZE_STREAM_DEFAULT_MODEL_ID);

  const [step, setStep] = useState<'upload' | 'analyzing' | 'configure'>('upload');
  const [thinking, setThinking] = useState('');

  const [analysis, setAnalysis] = useState<CsvAnalysis | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [uploadItems, setUploadItems] = useState<FileUploaderItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const handleHeaderNavigateAway = () => {
    if (step === 'analyzing') {
      analyzeStream.abort();
    }
    navigateToList();
  };

  const handleSelectFiles = (files: FileList) => {
    const file = files.item(0);
    if (!file) return;
    const itemId = `${Date.now()}-${file.name}`;
    setSelectedFile(file);
    setUploadItems([{ id: itemId, name: file.name, state: 'uploaded' }]);
    setAnalysis(null);
    resetColumnRelatedFields(reset, getValues);
  };

  const runAnalyze = (fileId: string, csvFileName: string) => {
    setThinking('');
    const handleError = (err: Error) => {
      const message = err.message || 'CSV の解析に失敗しました';
      toast.error(message);
      setStep('upload');
      const file = selectedFile;
      const currentItem = uploadItems[0];
      if (file) {
        const itemId = currentItem?.id ?? `${Date.now()}-${file.name}`;
        setUploadItems([{ id: itemId, name: file.name, state: 'error' }]);
      }
    };

    const applyAnalysisToForm = (next: CsvAnalysis) => {
      setAnalysis(next);
      const patch = buildConfigureFormPatchFromAnalysis(next, csvFileName);
      reset({
        ...getValues(),
        ...patch,
      });
      setStep('configure');
    };

    analyzeStream.start(isSystemAdmin ? { fileId, modelId: analyzeModelId } : { fileId }, {
      onEvent: (ev) => {
        if (ev.type === 'reasoning-delta') {
          setThinking((prev) => prev + ev.text);
        } else if (ev.type === 'done') {
          applyAnalysisToForm(ev.analysis);
        } else if (ev.type === 'error') {
          handleError(new Error(ev.message));
        }
      },
      onError: handleError,
    });
  };

  const handleStartUpload = async () => {
    const currentSourceSystemId = getValues('sourceSystemId');
    if (!currentSourceSystemId) {
      toast.error('ソースシステムを選択してください');
      return;
    }
    const file = selectedFile;
    if (!file) {
      toast.error('CSV ファイルを選択してください');
      return;
    }

    const currentItem = uploadItems[0];
    const itemId = currentItem?.id ?? `${Date.now()}-${file.name}`;
    setUploadItems([{ id: itemId, name: file.name, state: 'uploading' }]);
    setUploadProgress(0);

    try {
      const { fileId } = await uploadMutation.mutateAsync({
        projectId,
        file,
        onProgress: (ratio) => setUploadProgress(ratio),
      });
      setUploadedFileId(fileId);
      setUploadItems([{ id: itemId, name: file.name, state: 'uploaded' }]);
      setUploadProgress(null);
      setStep('analyzing');
      runAnalyze(fileId, file.name);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'アップロードに失敗しました';
      setUploadItems([{ id: itemId, name: file.name, state: 'error' }]);
      setUploadProgress(null);
      toast.error(message);
      // タイムアウト (AbortError) の場合のみサーバ側で file metadata / GCS オブジェクトが
      // 残っている可能性があるため一覧へ戻す。それ以外（ネットワーク／バリデーション等）は
      // 副作用が無く、ユーザーが修正して再試行できるようにその場に留める。
      if (err instanceof Error && err.name === 'AbortError') {
        navigateToList();
      }
    }
  };

  const handleDeleteUpload = (_fileId: string) => {
    setUploadItems([]);
    setSelectedFile(null);
    setUploadedFileId(null);
    setAnalysis(null);
    resetColumnRelatedFields(reset, getValues);
  };

  return {
    step,
    thinking,
    analysis,
    uploadMutation,
    analyzeStream,
    analyzeModelId,
    setAnalyzeModelId,
    uploadItems,
    selectedFile,
    uploadedFileId,
    uploadProgress,
    handleSelectFiles,
    handleDeleteUpload,
    handleStartUpload,
    handleHeaderNavigateAway,
  };
}
